import React from 'react';
import {
  Mail, MessageSquare, ArrowRight, ArrowLeft, Zap, Hand, FileText, CalendarClock, CalendarDays, GraduationCap, Target,
  ShoppingCart, CreditCard, Globe, Package, ShieldCheck, ShieldAlert, Hourglass, AtSign, AlertTriangle, CheckCircle2, Calculator, KeyRound, LifeBuoy, Lightbulb, ReceiptPoundSterling, Clock,
} from 'lucide-react';

// Consistent message glyph: email = envelope (emerald), text = speech bubble (sky).
export const MessageIcon = ({ channel, className = 'w-4 h-4' }) => (channel === 'sms'
  ? <MessageSquare className={`${className} text-sky-700`} />
  : <Mail className={`${className} text-emerald-700`} />);

const has = (s, ...words) => words.some((w) => s.includes(w));

/** Work out what fires a message from its key / trigger / timing text → { Icon, label, before } */
export function deriveTrigger(row) {
  const t = (row.timing || '').toLowerCase();
  const k = (row.key || '').toLowerCase();
  const trig = row.trigger || 'automatic';
  const daysBefore = t.match(/(\d+)\s*days? before/);
  if (daysBefore || has(t, 'day before', 'before the course', 'before insurance', 'before')) {
    const n = daysBefore ? `${daysBefore[1]} days before` : has(t, 'day before') ? 'Day before' : 'Before';
    if (has(t, k, 'insurance')) return { Icon: ShieldAlert, label: `${n} insurance expires`, before: true };
    return { Icon: GraduationCap, label: `${n} the course`, before: true };
  }
  if (has(t, 'days since', 'since the installer', 'after a lapse')) return { Icon: Hourglass, label: t.match(/(\d+) days/) ? `${t.match(/(\d+) days/)[1]} days inactive` : 'Activity resumes' };
  if (has(t, 'forgot password')) return { Icon: KeyRound, label: 'Forgot password' };
  if (has(t, 'ticket is raised')) return { Icon: LifeBuoy, label: 'Support ticket raised' };
  if (has(t, 'suggestion')) return { Icon: Lightbulb, label: 'Suggestion submitted' };
  if (trig === 'request' || has(t, 'form is submitted', 'form')) return { Icon: FileText, label: 'Form submitted' };
  if (has(t, 'daily', 'once a day', 'weekly', 'monthly', '1st of the month', 'weekdays', 'every')) {
    const label = has(t, 'daily', 'once a day') ? 'Daily' : has(t, 'weekly') ? 'Weekly' : has(t, 'monthly', '1st of') ? 'Monthly' : 'On a schedule';
    return { Icon: CalendarClock, label };
  }
  if (has(t, 'untouched for', 'hours')) return { Icon: Clock, label: 'Left untouched' };
  if (has(t, 'booked')) return { Icon: GraduationCap, label: 'Booked on a course' };
  if (has(t, 'move a booking')) return { Icon: CalendarDays, label: 'Course date moved' };
  if (has(t, 'lead')) return { Icon: Target, label: 'Lead assigned' };
  if (has(t, 'checkout', 'order is paid', 'order is submitted', 'order is placed')) return { Icon: has(t, 'paid') ? CreditCard : ShoppingCart, label: has(t, 'paid') ? 'Order paid' : 'Order placed' };
  if (has(t, 'await invoicing', 'invoice')) return { Icon: ReceiptPoundSterling, label: 'Awaiting invoice' };
  if (has(t, 'quote')) return { Icon: Calculator, label: 'Quote sent' };
  if (has(t, 'stock', 'delivery', 'warehouse')) return { Icon: Package, label: 'Stock event' };
  if (has(t, 'website')) return { Icon: Globe, label: 'Website order update' };
  if (has(t, 'approve now', 'stages are cleared', 'ox approved stage', 'promote')) return { Icon: ShieldCheck, label: 'Approval action' };
  if (has(t, '@mention')) return { Icon: AtSign, label: 'Colleague @mentioned' };
  if (has(t, 'fails', 'run out')) return { Icon: AlertTriangle, label: 'Delivery failure' };
  if (has(t, 'marked complete')) return { Icon: CheckCircle2, label: 'Job completed' };
  if (trig === 'staff') return { Icon: Hand, label: 'Staff press a button' };
  if (trig === 'installer') return { Icon: Hand, label: 'Installer taps a button' };
  return { Icon: Zap, label: 'Automatic event' };
}

/** [trigger] → [message]  or  [message] ← [date] for "before" messages. */
export function TriggerGlyph({ row, showLabel = true }) {
  const { Icon, label, before } = deriveTrigger(row);
  const trigCls = row.trigger === 'staff' ? 'bg-amber-100 text-amber-800' : row.trigger === 'installer' ? 'bg-teal-100 text-teal-800' : row.trigger === 'request' ? 'bg-slate-200 text-slate-700' : 'bg-violet-100 text-violet-800';
  const Box = ({ children, cls }) => <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md ${cls}`}>{children}</span>;
  return (
    <span className="inline-flex items-center gap-1.5" title={label} data-testid={`trigger-glyph-${row.key}`}>
      {before ? (
        <>
          <Box cls="bg-white border border-gray-200"><MessageIcon channel={row.channel} /></Box>
          <ArrowLeft className="w-3.5 h-3.5 text-gray-400" />
          <Box cls={trigCls}><Icon className="w-4 h-4" /></Box>
        </>
      ) : (
        <>
          <Box cls={trigCls}><Icon className="w-4 h-4" /></Box>
          <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
          <Box cls="bg-white border border-gray-200"><MessageIcon channel={row.channel} /></Box>
        </>
      )}
      {showLabel && <span className="text-xs font-semibold text-gray-800 ml-0.5">{label}</span>}
    </span>
  );
}
